/**
 * Language detection — pure function, no Vue dependencies.
 *
 * Priority (highest to lowest):
 * 1. ?lang=xx URL param (testing override)
 * 2. navigator.language (browser/OS setting)
 * 3. Referrer TLD heuristic
 * 4. 'en' default
 *
 * Note: <html lang> is NOT used as input — it is set as output
 * by the i18n module to reflect the detected language.
 */

/** Common TLD → language code mapping */
const tldMap: Record<string, string> = {
    de: 'de',
    fr: 'fr',
    es: 'es',
    it: 'it',
    nl: 'nl',
    pt: 'pt',
    pl: 'pl',
    se: 'sv',
    dk: 'da',
    no: 'no',
    fi: 'fi',
    cz: 'cs',
    jp: 'ja',
    kr: 'ko',
    cn: 'zh',
    tw: 'zh',
    ru: 'ru',
    br: 'pt',
    at: 'de',
    ch: 'de',
    be: 'nl',
    mx: 'es',
    ar: 'es',
    cl: 'es',
    co: 'es',
};

/**
 * Normalize a locale string, preserving region/script subtag with proper casing.
 *   'en-US'     → 'en-US'
 *   'pt_br'     → 'pt-BR'
 *   'FR'        → 'fr'
 *   'sr-Latn'   → 'sr-Latn'
 *   'zh-Hant-HK'→ 'zh-HK' (prefers 2-char region over 4-char script)
 */
function normalize(locale: string): string {
    const parts = locale.replace(/_/g, '-').split('-').filter(Boolean);
    if (parts.length === 0) return '';
    const lang = parts[0].toLowerCase().slice(0, 2);
    // Prefer 2-char region subtag (ISO 3166-1)
    for (let i = 1; i < parts.length; i++) {
        if (parts[i].length === 2) {
            return `${lang}-${parts[i].toUpperCase()}`;
        }
    }
    // Fall back to 4-char script subtag (ISO 15924) — e.g., Latn, Hant
    for (let i = 1; i < parts.length; i++) {
        if (parts[i].length === 4) {
            const script = parts[i].charAt(0).toUpperCase() + parts[i].slice(1).toLowerCase();
            return `${lang}-${script}`;
        }
    }
    return lang;
}

/** Extract base language code from a normalized locale: 'pt-BR' → 'pt' */
function baseLang(locale: string): string {
    return locale.split('-')[0];
}

/**
 * The visitor's browser locale, normalized with its region preserved
 * (e.g. 'en-US', 'pt-BR'). Used for date/number formatting so we honour the
 * visitor's regional conventions even when the *content* language collapses to
 * a 2-char pack (e.g. an American sees US time formats on an English room).
 * Returns '' when unavailable (SSR / no navigator).
 */
export function detectRegionLocale(): string {
    try {
        return navigator.language ? normalize(navigator.language) : '';
    } catch {
        return '';
    }
}

/**
 * Extract language hint from referrer TLD.
 * e.g. "https://shop.example.de/page" → 'de'
 */
function langFromReferrer(): string | null {
    try {
        const ref = document.referrer;
        if (!ref) return null;
        const hostname = new URL(ref).hostname;
        const tld = hostname.split('.').pop() || '';
        return tldMap[tld] || null;
    } catch {
        return null;
    }
}

/**
 * Detect the user's preferred language.
 *
 * @param availableLocales - Set of locale codes the customer has provided packs for.
 *                           If empty/undefined, detection still runs and returns best guess.
 * @returns 2-char lowercase language code
 */
export function detectLanguage(availableLocales?: Set<string>): string {
    const candidates: string[] = [];

    // Push both the full form (e.g., 'pt-BR') and the base (e.g., 'pt'),
    // so regional variants win if available but we fall through to the base.
    const pushWithFallback = (raw: string) => {
        const full = normalize(raw);
        if (!full) return;
        candidates.push(full);
        const base = baseLang(full);
        if (base && base !== full) candidates.push(base);
    };

    // 1. ?lang=xx URL param (testing override)
    try {
        const param = new URLSearchParams(window.location.search).get('lang');
        if (param) pushWithFallback(param);
    } catch { /* SSR / no window */ }

    // 2. navigator.language (browser/OS setting)
    try {
        if (navigator.language) pushWithFallback(navigator.language);
    } catch { /* SSR */ }

    // 3. Referrer TLD (always base-only)
    const refLang = langFromReferrer();
    if (refLang) candidates.push(refLang);

    // 4. Default
    candidates.push('en');

    // If we have available packs, pick the first candidate that matches
    if (availableLocales && availableLocales.size > 0) {
        for (const c of candidates) {
            if (availableLocales.has(c)) return c;
        }
        // No match — fall back to 'en'
        return 'en';
    }

    // No packs loaded — return best candidate
    return candidates[0];
}
